import React from 'react';
import { Button, Divider, Popconfirm, Space, Tooltip, Typography, message } from 'antd';
import { CloseOutlined, DeleteOutlined, PaperClipOutlined, UploadOutlined } from '@ant-design/icons';
import { LoadingOutlined } from '@ant-design/icons';
import { UploadFile } from '@partsbadger/library';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { is3dFileAllowedForManufacturingType, isStlFile, STL_MANUFACTURING_TYPE } from '@partsbadger/utils';
import { QuoteStore } from '@/stores';
import {
    STL_PROCESS_LOCKED_MESSAGE,
} from '@/utils/stlManufacturing';
const { Link, Text } = Typography;

const ACCEPT_3D = [
    'step',
    'stp',
    'sldprt',
    'prt',
    'stl',
    'iges',
    'igs',
    'ipt',
    'x_t',
    'catia',
    'catpart',
    'catproduct',
];

const getAccepted3dExtensions = (manufacturingType?: string) => {
    if (!manufacturingType || manufacturingType === STL_MANUFACTURING_TYPE) {
        return ACCEPT_3D;
    }

    return ACCEPT_3D.filter(extension => extension !== 'stl');
};

type UploadedFileItem = {
    status?: 'uploading' | 'done' | 'error' | 'removed' | string;
    response?: {
        id?: number;
    };
};

export interface FileInfoPillProps {
    stepFile: {
        name: string;
        url: string;
    } | null;
    drawingFile?: {
        name: string;
        url: string;
    } | null;
    dimensions?: string | null;
    className?: string;
    fullWidth?: boolean;
    onFileUpload: (file_type: 'file_3d' | 'file_2d', file_id: number, fileName?: string) => void;
    has3DFileAnalysisError: boolean;
    onRemoveField?: (name_field: string) => void;
    isHobbyist?: boolean;
    lockedFileUpdateAction?: React.ReactNode;
    lockFiles?: boolean;
    manufacturingType?: string;
}

export const FileInfoPill: React.FC<FileInfoPillProps> = ({
    stepFile,
    drawingFile,
    dimensions,
    className,
    fullWidth = false,
    onFileUpload,
    has3DFileAnalysisError,
    onRemoveField,
    isHobbyist = false,
    lockedFileUpdateAction,
    lockFiles = false,
    manufacturingType,
}) => {
    const accepted3dExtensions = getAccepted3dExtensions(manufacturingType);

    const handle3dBeforeUpload = (file: { name: string }) => {
        if (!is3dFileAllowedForManufacturingType(file.name, manufacturingType)) {
            message.warning(STL_PROCESS_LOCKED_MESSAGE, 5);
            return false;
        }

        return true;
    };

    const handle3dUploadChange = (
        fileList: Array<UploadedFileItem & { name?: string }>,
        onSuccess: (fileId: number, fileName?: string) => void
    ) => {
        const latestFile = fileList[fileList.length - 1];
        const fileName = latestFile?.name;

        if (fileName && !is3dFileAllowedForManufacturingType(fileName, manufacturingType)) {
            message.warning(STL_PROCESS_LOCKED_MESSAGE, 5);
            return;
        }

        const response = latestFile?.response;
        if (response?.id) {
            onSuccess(response.id, fileName);
        }
    };
    const text = `${dimensions ? ` • ${dimensions}` : ''}`;
    const [isUploading2D, setIsUploading2D] = React.useState(false);
    const handle2DChange = (fileList: Array<UploadedFileItem>) => {
        const latestFile = fileList[fileList.length - 1];
        const status = latestFile?.status;

        if (status === 'uploading') {
            setIsUploading2D(true);
            return;
        }

        if (status === 'error' || status === 'removed') {
            setIsUploading2D(false);
            return;
        }

        if (status === 'done' && latestFile?.response?.id) {
            onFileUpload('file_2d', latestFile.response.id);
        }
    };

    const AnalysisError = () => (
        <Tooltip title="Our system had an issue while analyzing this 3D file. You can continue and submit it for review by a Design Engineer, or upload a new file.">
            <div
                style={{
                    marginTop: 4,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 12,
                    color: 'red',
                }}
            >
                <ExclamationCircleOutlined />
                <span>3D analysis failed</span>
            </div>
        </Tooltip>
    );

    const renderReadOnlyDrawingFile = () => (
        <div className="flex flex-row items-center gap-1 sm:gap-2 min-w-0">
            <span className="text-xs font-medium text-slate-500 shrink-0">2D file:</span>
            <Space size="small" wrap={true} align="baseline" className="w-full sm:w-auto min-w-0">
                {drawingFile?.name ? (
                    <Tooltip title="File 2D">
                        <div className="flex items-center justify-center">
                            <PaperClipOutlined />
                            &nbsp;
                            <Link type="secondary" href={drawingFile.url} download ellipsis style={{ maxWidth: 180 }}>
                                {drawingFile.name}
                            </Link>
                        </div>
                    </Tooltip>
                ) : (
                    <Tooltip title="No 2D drawing provided">
                        <div className="flex items-center justify-center">
                            <ExclamationCircleOutlined
                                style={{
                                    color: '#fa8c16',
                                }}
                            />{' '}
                            &nbsp;
                            <Text> &nbsp; 2D Pending</Text>
                        </div>
                    </Tooltip>
                )}
            </Space>
        </div>
    );

    return (
        <div className={`${fullWidth ? 'w-full' : ''} min-w-0 ${className || ''}`}>
            {/*<div className="inline-flex items-start sm:items-center gap-2 text-xs text-slate-500 bg-slate-50 rounded px-2 py-1.5 min-w-0 max-w-full">*/}
            {/*    <FileTextOutlined className="flex flex-shrink-0 mt-0.5 sm:mt-0" />*/}
            {/*    <Tooltip title={<span className="text-xs">{text}</span>}>*/}
            {/*        <div className="min-w-0 max-w-full text-[11px] sm:text-sm text-slate-500 leading-snug whitespace-normal break-words">*/}
            {/*            {text}*/}
            {/*        </div>*/}
            {/*    </Tooltip>*/}
            {/*</div>*/}
            {/* Files */}

            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-0 min-w-0">
                {/* 3D file */}
                <div className="flex flex-row items-center gap-1 sm:gap-2 min-w-0">
                    <span className="text-xs font-medium text-slate-500 shrink-0">3D file:</span>
                    <Space size="small" className="w-full sm:w-auto min-w-0">
                        <div className={'flex items-center justify-center '}>
                            {stepFile ? (
                                <div className={'flex items-center justify-center '}>
                                    <PaperClipOutlined /> &nbsp;
                                    <div style={{ maxWidth: 220 }}>
                                        <Tooltip title={`File 3D`}>
                                            <Link type={'secondary'} href={stepFile.url} download ellipsis>
                                                {stepFile.name}
                                            </Link>
                                        </Tooltip>

                                        {has3DFileAnalysisError ? <AnalysisError /> : null}
                                    </div>
                                    {has3DFileAnalysisError ? (
                                        <Tooltip title="Upload 3D file">
                                            <UploadFile
                                                fileType="3D"
                                                accept={accepted3dExtensions}
                                                renderAs="button"
                                                showUploadList={true}
                                                beforeUpload={handle3dBeforeUpload}
                                                onChange={fileList => {
                                                    handle3dUploadChange(fileList, (fileId, fileName) => {
                                                        onFileUpload('file_3d', fileId, fileName);
                                                    });
                                                }}
                                            >
                                                <Button type="text" size="small" icon={<UploadOutlined />} />
                                            </UploadFile>
                                        </Tooltip>
                                    ) : null}
                                </div>
                            ) : (
                                <div className={'flex items-center justify-center '}>
                                    <Tooltip title="No 3D drawing provided.">
                                        <div className={'flex items-center justify-center '}>
                                            <ExclamationCircleOutlined
                                                style={{
                                                    color: isHobbyist ? 'red' : '#fa8c16',
                                                }}
                                            />{' '}
                                            &nbsp;
                                            <span
                                                style={{
                                                    color: isHobbyist ? 'red' : '#fa8c16',
                                                }}
                                            >
                                                3D Pending
                                            </span>
                                        </div>
                                    </Tooltip>
                                    &nbsp;
                                    <Tooltip title="Upload 3D file">
                                            <UploadFile
                                                fileType="3D"
                                                renderAs="button"
                                                accept={accepted3dExtensions}
                                                showUploadList={true}
                                                beforeUpload={handle3dBeforeUpload}
                                                onChange={fileList => {
                                                    handle3dUploadChange(fileList, (fileId, fileName) => {
                                                        onFileUpload('file_3d', fileId, fileName);
                                                    });
                                                }}
                                            customUrl={`customer/quote/${QuoteStore.getQuoteUid}/3d-file/`}
                                        >
                                            <Button type="text" size="small" icon={<UploadOutlined />} />
                                        </UploadFile>
                                    </Tooltip>
                                </div>
                            )}
                        </div>
                    </Space>
                </div>

                <div className="hidden sm:block self-stretch">
                    <Divider type="vertical" />
                </div>

                {lockFiles ? (
                    <>
                        {renderReadOnlyDrawingFile()}
                        <div className="flex flex-row items-center gap-1 sm:gap-2 min-w-0">
                            {lockedFileUpdateAction}
                        </div>
                    </>
                ) : (
                    /* Drawing file */
                    <div className="flex flex-row items-center gap-1 sm:gap-2 min-w-0">
                        <span className="text-xs font-medium text-slate-500 shrink-0">2D file:</span>
                        <Space size="small" wrap={true} align="baseline" className="w-full sm:w-auto min-w-0">
                            {drawingFile?.name ? (
                                <div className={'flex items-center justify-center '}>
                                    <>
                                        {isUploading2D ? (
                                            <Text type="secondary">
                                                <LoadingOutlined /> &nbsp; Uploading 2D...
                                            </Text>
                                        ) : (
                                            <Tooltip title={'File 2D'}>
                                                <div className={'flex items-center justify-center '}>
                                                    <PaperClipOutlined />
                                                    &nbsp;
                                                    <Link
                                                        type={'secondary'}
                                                        href={drawingFile.url}
                                                        download
                                                        ellipsis
                                                        style={{ maxWidth: 180 }}
                                                    >
                                                        {drawingFile.name}
                                                    </Link>
                                                </div>
                                            </Tooltip>
                                        )}

                                        <Tooltip title="Upload drawing file">
                                            <UploadFile
                                                fileType="2D"
                                                renderAs="button"
                                                showUploadList={false}
                                                onChange={handle2DChange}
                                            >
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<UploadOutlined />}
                                                    disabled={isUploading2D}
                                                />
                                            </UploadFile>
                                        </Tooltip>

                                        <Popconfirm
                                            title="Remove 2D file?"
                                            okText="Remove"
                                            cancelText="Cancel"
                                            disabled={isUploading2D}
                                            onConfirm={() => {
                                                if (onRemoveField) onRemoveField('file_2d');
                                            }}
                                        >
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<CloseOutlined />}
                                                disabled={isUploading2D}
                                                style={{
                                                    marginLeft: -12,
                                                }}
                                                title="Remove drawing file"
                                            />
                                        </Popconfirm>
                                    </>
                                </div>
                            ) : (
                                <div className={'flex items-center justify-center '}>
                                    <Tooltip title="No 2D drawing provided">
                                        <div className={'flex items-center justify-center '}>
                                            {isUploading2D ? (
                                                <LoadingOutlined
                                                    style={{
                                                        color: '#1677ff',
                                                    }}
                                                />
                                            ) : (
                                                <ExclamationCircleOutlined
                                                    style={{
                                                        color: '#fa8c16',
                                                    }}
                                                />
                                            )}{' '}
                                            &nbsp;
                                            <Text> &nbsp; {isUploading2D ? 'Uploading 2D...' : '2D Pending'}</Text>
                                        </div>
                                    </Tooltip>

                                    <Tooltip title="Upload drawing file">
                                        <UploadFile
                                            fileType="2D"
                                            renderAs="button"
                                            showUploadList={false}
                                            onChange={handle2DChange}
                                        >
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<UploadOutlined />}
                                                disabled={isUploading2D}
                                            />
                                        </UploadFile>
                                    </Tooltip>
                                </div>
                            )}
                        </Space>
                        {lockedFileUpdateAction}
                    </div>
                )}
            </div>
        </div>
    );
};
